/*
 * BAS /build configurator — Spec §9.5.
 *
 * State + URL sync + single multi-line ATC.
 * - URL params drive initial state and update on every change (history.replaceState).
 * - Price total recalculates in <100ms per spec acceptance.
 * - XLINK 4 is gated to X2-only.
 * - Single POST to /cart/add.js with items[] adds all selected SKUs as one transaction.
 * - After ATC, opens existing cart drawer (modal-cart) if cart_type === 'modal',
 *   else redirects to /cart.
 *
 * Loaded as type=module so it can pick up cart-store via the theme's importmap if needed.
 */

import Cart from 'cart-store';

(function () {
  'use strict';

  const root = document.querySelector('[data-bas-build]');
  if (!root) return;

  const configEl = root.querySelector('[data-bas-build-config]');
  if (!configEl) return;

  const config = JSON.parse(configEl.textContent);
  const products = config.products || {};
  let isAdding = false;

  // ----- State -----
  const state = {
    system: null,        // 'deluxe' | 'x2' | null
    xlink: '0',          // '0' | '2' | '4'
    training: 'no',      // 'no' | 'yes'
    weightbags: false,
    focuspads: false,
  };

  // ----- Helpers -----
  function formatMoney(cents) {
    // Simple USD formatter; matches Shopify {{ price | money_without_trailing_zeros }} for most cases.
    const dollars = cents / 100;
    return dollars % 1 === 0
      ? `$${dollars.toFixed(0)}`
      : `$${dollars.toFixed(2)}`;
  }

  function readUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const model = params.get('model');
    if (model === 'deluxe' || model === 'x2') state.system = model;
    const xlink = params.get('xlink');
    if (['0', '2', '4'].includes(xlink)) state.xlink = xlink;
    const training = params.get('training');
    if (training === 'all-access' || training === 'yes') state.training = 'yes';
    const weightbags = params.get('weightbags');
    if (weightbags === '1' || weightbags === 'true') state.weightbags = true;
    const focuspads = params.get('focuspads');
    if (focuspads === '1' || focuspads === 'true') state.focuspads = true;
  }

  function writeUrlParams() {
    const params = new URLSearchParams(window.location.search);
    ['model', 'xlink', 'training', 'weightbags', 'focuspads'].forEach((key) => params.delete(key));
    if (state.system) params.set('model', state.system);
    if (state.xlink !== '0') params.set('xlink', state.xlink);
    if (state.training === 'yes') params.set('training', 'all-access');
    if (state.weightbags) params.set('weightbags', '1');
    if (state.focuspads) params.set('focuspads', '1');
    const qs = params.toString();
    const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
    history.replaceState(history.state, '', newUrl);
  }

  function systemProducts(system = state.system, xlink = state.xlink) {
    if (system === 'deluxe' && xlink === '2' && products.deluxeXlink2Bundle) {
      return [products.deluxeXlink2Bundle];
    }
    if (system === 'x2' && xlink === '4' && products.x2Xlink4Bundle) {
      return [products.x2Xlink4Bundle];
    }
    const selected = [];
    if (system) selected.push(products[system]);
    if (xlink !== '0') selected.push(products[`xlink${xlink}`]);
    return selected;
  }

  function isAvailable(product) {
    return !!(product && product.variantId && product.available === true);
  }

  function selectionAvailable() {
    const selected = systemProducts();
    if (state.training === 'yes') selected.push(products.allaccess);
    if (state.weightbags) selected.push(products.weightbags);
    if (state.focuspads) selected.push(products.focuspads);
    return selected.length > 0 && selected.every(isAvailable);
  }

  // ----- Render -----
  function applyStateToUI() {
    // System
    root.querySelectorAll('[data-bas-option="system"]').forEach((el) => {
      const isMatch = el.dataset.value === state.system;
      const input = el.querySelector('input');
      const xlink = el.dataset.value === 'deluxe' && state.xlink === '4' ? '0' : state.xlink;
      const disabled = !systemProducts(el.dataset.value, xlink).every(isAvailable);
      el.dataset.disabled = disabled ? 'true' : 'false';
      if (input) {
        input.checked = isMatch;
        input.disabled = disabled;
      }
    });

    // XLINK — also gate XLINK 4
    root.querySelectorAll('[data-bas-option="xlink"]').forEach((el) => {
      const isMatch = el.dataset.value === state.xlink;
      const input = el.querySelector('input');
      const requiresSystem = el.dataset.requiresSystem;
      const gated = requiresSystem && state.system !== requiresSystem;
      const disabled = gated || (el.dataset.value !== '0' && !systemProducts(state.system, el.dataset.value).every(isAvailable));
      el.dataset.disabled = disabled ? 'true' : 'false';
      if (input) {
        input.disabled = !!disabled;
        input.checked = !gated && isMatch;
      }
    });

    // If state.xlink is gated and disabled, force to '0'
    const xlinkOption = root.querySelector(`[data-bas-option="xlink"][data-value="${state.xlink}"]`);
    if (xlinkOption && xlinkOption.dataset.requiresSystem && xlinkOption.dataset.requiresSystem !== state.system) {
      state.xlink = '0';
      applyStateToUI();
      return;
    }

    updateXlinkOptionPrices();

    // Training
    root.querySelectorAll('[data-bas-option="training"]').forEach((el) => {
      const input = el.querySelector('input');
      const disabled = el.dataset.value === 'yes' && !isAvailable(products.allaccess);
      el.dataset.disabled = disabled ? 'true' : 'false';
      if (input) {
        input.checked = el.dataset.value === state.training;
        input.disabled = disabled;
      }
    });

    // Accessories
    const wbInput = root.querySelector('[data-bas-option="weightbags"] input');
    if (wbInput) {
      wbInput.checked = state.weightbags;
      wbInput.disabled = !state.weightbags && !isAvailable(products.weightbags);
    }
    const fpInput = root.querySelector('[data-bas-option="focuspads"] input');
    if (fpInput) {
      fpInput.checked = state.focuspads;
      fpInput.disabled = !state.focuspads && !isAvailable(products.focuspads);
    }
  }

  function buildCart() {
    // Returns { items: [{id, quantity}], total: cents, summaryLines: [{label, price, note?}] }
    const items = [];
    const summary = [];
    let total = 0;

    // ----- System + XLINK -----
    // If the selection matches a known bundle, add the BUNDLE SKU as a single
    // line item at the bundle price (which is less than the sum of standalone
    // prices — see memory/project_configurator_pricing.md for the economics).
    // Otherwise add the system + per-tracker XLINK individually.
    const xlinkQty = parseInt(state.xlink, 10);

    if (state.system === 'deluxe' && state.xlink === '2' && products.deluxeXlink2Bundle) {
      items.push({ id: products.deluxeXlink2Bundle.variantId, quantity: 1 });
      summary.push({
        label: products.deluxeXlink2Bundle.title || 'Deluxe + XLINK 2 Bundle',
        price: products.deluxeXlink2Bundle.price,
        note: 'Bundle price — includes 2 XLINK trackers'
      });
      total += products.deluxeXlink2Bundle.price;
    } else if (state.system === 'x2' && state.xlink === '4' && products.x2Xlink4Bundle) {
      items.push({ id: products.x2Xlink4Bundle.variantId, quantity: 1 });
      summary.push({
        label: products.x2Xlink4Bundle.title || 'X2 + XLINK 4 Bundle',
        price: products.x2Xlink4Bundle.price,
        note: 'Bundle price — includes 4 XLINK trackers'
      });
      total += products.x2Xlink4Bundle.price;
    } else {
      // Non-bundle configuration: add system + individual XLINK trackers.
      if (state.system === 'deluxe' && products.deluxe) {
        items.push({ id: products.deluxe.variantId, quantity: 1 });
        summary.push({ label: products.deluxe.title, price: products.deluxe.price });
        total += products.deluxe.price;
      } else if (state.system === 'x2' && products.x2) {
        items.push({ id: products.x2.variantId, quantity: 1 });
        summary.push({ label: products.x2.title, price: products.x2.price });
        total += products.x2.price;
      }

      const xlinkProduct = state.xlink === '2'
        ? products.xlink2
        : (state.xlink === '4' ? products.xlink4 : null);

      if (xlinkQty > 0 && xlinkProduct) {
        items.push({ id: xlinkProduct.variantId, quantity: 1 });
        summary.push({ label: xlinkProduct.title || `XLINK Tracker × ${xlinkQty}`, price: xlinkProduct.price });
        total += xlinkProduct.price;
      }
    }

    if (state.training === 'yes' && products.allaccess) {
      items.push({ id: products.allaccess.variantId, quantity: 1 });
      const hasBundleDiscount = state.system
        && products.allaccess.bundlePrice > 0
        && products.allaccess.bundlePrice < products.allaccess.price;
      const displayPrice = hasBundleDiscount ? products.allaccess.bundlePrice : products.allaccess.price;
      summary.push({
        label: products.allaccess.title,
        price: displayPrice,
        note: hasBundleDiscount ? 'Bundle price — discount applies at checkout' : null
      });
      total += displayPrice;
    }

    if (state.weightbags && products.weightbags) {
      items.push({ id: products.weightbags.variantId, quantity: 1 });
      summary.push({ label: products.weightbags.title, price: products.weightbags.price });
      total += products.weightbags.price;
    }

    if (state.focuspads && products.focuspads) {
      items.push({ id: products.focuspads.variantId, quantity: 1 });
      summary.push({ label: products.focuspads.title, price: products.focuspads.price });
      total += products.focuspads.price;
    }

    return { items, total, summary };
  }

  function getXlinkDisplayPrice(value) {
    if (value === '2') {
      if (state.system === 'deluxe' && products.deluxe && products.deluxeXlink2Bundle) {
        return Math.max(products.deluxeXlink2Bundle.price - products.deluxe.price, 0);
      }
      return products.xlink2 ? products.xlink2.price : 0;
    }

    if (value === '4') {
      if (state.system === 'x2' && products.x2 && products.x2Xlink4Bundle) {
        return Math.max(products.x2Xlink4Bundle.price - products.x2.price, 0);
      }
      return products.xlink4 ? products.xlink4.price : 0;
    }

    return 0;
  }

  function updateXlinkOptionPrices() {
    root.querySelectorAll('[data-bas-build-xlink-price]').forEach((el) => {
      const value = el.dataset.basBuildXlinkPrice;
      const price = getXlinkDisplayPrice(value);
      el.dataset.basPrice = String(price);
      el.textContent = `+${formatMoney(price)}`;
    });
    const trainingPrice = root.querySelector('[data-bas-build-training-price]');
    if (trainingPrice && products.allaccess) {
      const price = state.system && products.allaccess.bundlePrice > 0
        ? Math.min(products.allaccess.price, products.allaccess.bundlePrice)
        : products.allaccess.price;
      trainingPrice.textContent = `+${formatMoney(price)}`;
    }
  }

  function renderSummary() {
    const { items, total, summary } = buildCart();
    const totalElements = root.querySelectorAll('[data-bas-build-total]');
    totalElements.forEach((el) => { el.textContent = formatMoney(total); });

    const summaryEl = root.querySelector('[data-bas-build-summary]');
    if (summaryEl) {
      if (items.length === 0) {
        summaryEl.innerHTML = '<li class="bas-build__summary-empty">Pick a system above to get started.</li>';
      } else {
        summaryEl.innerHTML = summary.map((line) => `
          <li class="bas-build__summary-line">
            <span><strong>${escapeHtml(line.label)}</strong>${line.note ? `<br><span class="bas-build__summary-line-bundle-note">${escapeHtml(line.note)}</span>` : ''}</span>
            <span>${formatMoney(line.price)}</span>
          </li>
        `).join('');
      }
    }

    const atcButtons = root.querySelectorAll('[data-bas-build-atc]');
    atcButtons.forEach((btn) => {
      btn.disabled = isAdding || items.length === 0 || !selectionAvailable();
    });
    const availability = root.querySelector('[data-bas-build-availability]');
    if (availability) {
      const hasSelection = state.system || state.xlink !== '0' || state.training === 'yes' || state.weightbags || state.focuspads;
      availability.hidden = !hasSelection || selectionAvailable();
    }

    // Sticky bar visibility — show as soon as system is picked
    const sticky = root.querySelector('[data-bas-build-sticky]');
    const stickyCount = root.querySelector('[data-bas-build-sticky-count]');
    if (sticky && stickyCount) {
      if (items.length > 0) {
        sticky.hidden = false;
        const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
        stickyCount.textContent = `${itemCount} item${itemCount === 1 ? '' : 's'}`;
      } else {
        sticky.hidden = true;
      }
    }
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // ----- Events -----
  function bind() {
    // System selection
    root.querySelectorAll('[data-bas-option="system"]').forEach((el) => {
      el.addEventListener('click', () => {
        if (el.dataset.disabled === 'true') return;
        state.system = el.dataset.value;
        afterChange();
      });
    });

    // XLINK selection
    root.querySelectorAll('[data-bas-option="xlink"]').forEach((el) => {
      el.addEventListener('click', () => {
        if (el.dataset.disabled === 'true') return;
        state.xlink = el.dataset.value;
        afterChange();
      });
    });

    // Training selection
    root.querySelectorAll('[data-bas-option="training"]').forEach((el) => {
      el.addEventListener('click', () => {
        if (el.dataset.disabled === 'true') return;
        state.training = el.dataset.value;
        afterChange();
      });
    });

    // Accessories
    const wbInput = root.querySelector('[data-bas-option="weightbags"] input');
    if (wbInput) {
      wbInput.addEventListener('change', () => {
        state.weightbags = wbInput.checked;
        afterChange();
      });
    }
    const fpInput = root.querySelector('[data-bas-option="focuspads"] input');
    if (fpInput) {
      fpInput.addEventListener('change', () => {
        state.focuspads = fpInput.checked;
        afterChange();
      });
    }

    // Most Popular pre-fill
    const popularBtn = root.querySelector('[data-bas-build-popular-pick]');
    if (popularBtn) {
      popularBtn.addEventListener('click', () => {
        state.system = 'x2';
        state.xlink = '4';
        state.training = 'yes';
        afterChange();
        // Scroll the user to the review step so they see the total before clicking ATC.
        const reviewStep = root.querySelector('[data-bas-build-step="review"]');
        if (reviewStep) reviewStep.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // ATC (both inline and sticky)
    root.querySelectorAll('[data-bas-build-atc]').forEach((btn) => {
      btn.addEventListener('click', onAtc);
    });
  }

  function afterChange() {
    applyStateToUI();
    renderSummary();
    writeUrlParams();
  }

  // ----- ATC -----
  async function onAtc(e) {
    e.preventDefault();
    if (isAdding || !selectionAvailable()) return;
    const { items } = buildCart();
    if (items.length === 0) return;
    isAdding = true;

    const atcButtons = root.querySelectorAll('[data-bas-build-atc]');
    const errorEl = root.querySelector('[data-bas-build-atc-error]');
    atcButtons.forEach((b) => { b.classList.add('is-loading'); b.disabled = true; });
    if (errorEl) errorEl.hidden = true;

    try {
      // CartStore.addVariantsToCart accepts an array shaped like:
      //   [{ id, quantity, properties? }]
      // and POSTs to /cart/add.js as a single transaction.
      await Cart.add(items);

      // Success → open cart drawer (existing modal-cart) or redirect to /cart.
      if (config.isCartDrawer) {
        const trigger = document.querySelector('[data-a11y-dialog-show="modal-cart"]');
        if (trigger) {
          trigger.click();
        } else {
          window.location.href = config.cartUrl;
        }
      } else {
        window.location.href = config.cartUrl;
      }
    } catch (err) {
      console.error('[bas-build] ATC failed:', err);
      if (errorEl) {
        errorEl.textContent = (err && err.message) ? err.message : 'Add to cart failed. Please try again.';
        errorEl.hidden = false;
      }
    } finally {
      isAdding = false;
      atcButtons.forEach((b) => { b.classList.remove('is-loading'); });
      renderSummary(); // restores disabled state for empty configs
    }
  }

  // ----- Init -----
  readUrlParams();
  applyStateToUI();
  renderSummary();
  bind();
})();
